import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import Table, { type Column } from './Table';
import Pagination from './Pagination';
import Card from './Card';

export interface ServerSideTableProps<T extends Record<string, any>> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage: string;
  total?: number | null;
  currentPage: number;
  pageSize: number;
  canGoNext: boolean;
  canGoPrevious: boolean;
  onPageChange: (direction: 'next' | 'prev' | 'first' | 'last') => void;
  onPageSizeChange: (size: number) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

export default function ServerSideTable<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  emptyMessage,
  total,
  currentPage,
  pageSize,
  canGoNext,
  canGoPrevious,
  onPageChange,
  onPageSizeChange,
  sorting,
  onSortingChange,
}: ServerSideTableProps<T>) {
  const totalPages =
    total !== null && total !== undefined ? Math.ceil(total / pageSize) : null;

  if (data.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <svg
            className="mx-auto h-16 w-16 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <p className="text-lg font-semibold text-gray-700 mb-2">{emptyMessage}</p>
          <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Table
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        emptyMessage={emptyMessage}
        enableSorting
        enablePagination={false}
        total={total ?? null}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        total={total ?? null}
        pageSize={pageSize}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
}
